/* eslint-disable @typescript-eslint/no-use-before-define */
import { isEmpty } from 'ramda';
import debounce from 'lodash.debounce';
import apigateway from '../utils/apigateway';
import { Discount, Sale, SaleItem } from '../pages/POSTeller/POSTeller';
import { Product } from '../pages/Inventory/Inventory';

/**
 *  ----------------------------------------------------------------------------
 *  ----------------------------------------------------------------------------
 * This has nothing to do with the conventional Context API.
 * It just contains helper functions for actions such as api calls.
 * If you find a better way to group this functions or methods feel free to
 * refactor and improve the code.
 *
 * Please dont misuse the FREEDOM 😉
 * ----------------------------------------------------------------------------
 * ----------------------------------------------------------------------------
 */

export type Tax = {
  id: number;
  name: string;
  description: string;
  rate: number;
  groupings: Array<{ taxId: number; applicableOn: string }> | null;
};

export type Category = {
  id: number;
  name: string;
  parentId: number;
  description: string;
  icon: string;
};

export type Brand = {
  id: number;
  name: string;
  description: string;
  logo: string;
};

export type Department = {
  id: number;
  name: string;
  description: string;
};

export type ProductStatus = {
  name: string;
};

export type StorePreferences = {
  categories: Category[];
  brands: Brand[];
  departments: Department[];
  taxes: Tax[];
  productStatuses: ProductStatus[];
  cashRegisters: CashRegister[];
  showTourGuide: boolean;
};

export type DashboardStats = {
  monthSummary: {
    month: string;
    sales: number;
    salesCount: number;
    cost: number;
    profit: number;
  };
  sales: number;
  transactions: number;
  productsSold: number;
  inventoryInStock: number;
  hourlySales: Array<{
    hour: string;
    sale: number;
  }>;
  topProducts: Array<{
    name: string;
    quantitySold: number;
    salesCount: number;
    sales: number;
    profit: number;
  }>;
  topCustomers: Array<{
    name: string;
    productsPurchased: number;
    total: number;
  }>;
};

export type SearchQueryType = {
  q: string;
  order: string;
  filter: Record<string, unknown>;
  count: number;
  page?: number;
};

export type CashRegister = {
  id: number;
  name: string;
  description: string;
  isPrimary: boolean;
  isOpenable: boolean;
  histories: CashRegisterHistory[];
  createdAt: string;
};

export type CashRegisterHistory = {
  id: number;
  openingTime: string;
  closingTime: string;
  talliedSalesPayments: TalliedSalesPayment[];
  cashEntries: Array<Record<string, number>>;
  notes: string;
  openedBy: {
    userId: string;
    name: string;
  };
};

export type TalliedSalesPayment = {
  tenderType: 'mobile-money' | 'cash' | 'card';
  float: number;
  payments: number;
  withdraw: number;
  expected: number;
  counted: number;
  difference: number;
};

export type PaymentPlan = {
  id: number;
  name: string;
  productsLimit: number;
  salesLimit: number;
  price: number;
  displayPrice: number;
  currency: string;
  nextChargeDate: string | null;
  paymentDetails: {
    lastTransactionId: string;
    reusable: boolean | null;
    channel: string | null;
    bank: string | null;
    bin: string | null;
    brand: string | null;
    last4: string | null;
  } | null;
  metadata: {
    additional: {
      price: number;
      salesLimit: number;
      displayPrice: number;
    };
  };
};

export type Layaway = {
  id: number;
  customerId: string;
  userId: string;
  customer: string;
  user: string;
  isPrimary: boolean;
  sale: Sale;
  status: 'Ongoing' | 'Complete';
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
};

export type Country = {
  name: string;
  slug: string;
  iso: string;
  iso3: string;
  currency: string;
  currencySymbol: string;
  flag?: string;
};

export type StoreMeta = {
  language: string;
  dateFormat: string;
  notifications: {
    outOfStockAlert: boolean;
    productSoldAlert: boolean;
  };
  inActiveTimer: string;
};

const month = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const initialDashboardStats = {
  monthSummary: {
    month: month[new Date().getMonth()],
    sales: 0.0,
    salesCount: 0.0,
    cost: 0.0,
    profit: 0.0,
  },
  sales: 0.0,
  transactions: 0.0,
  productsSold: 0.0,
  inventoryInStock: 0,
  hourlySales: [
    {
      hour: '8:00',
      sale: 0,
    },
    {
      hour: '9:00',
      sale: 0,
    },
    {
      hour: '10:00',
      sale: 0,
    },
    {
      hour: '11:00',
      sale: 0,
    },
    {
      hour: '12:00',
      sale: 0,
    },
    {
      hour: '13:00',
      sale: 0,
    },
    {
      hour: '14:00',
      sale: 0,
    },
    {
      hour: '15:00',
      sale: 0,
    },
    {
      hour: '16:00',
      sale: 0,
    },
    {
      hour: '17:00',
      sale: 0,
    },
    {
      hour: '18:00',
      sale: 0,
    },
    {
      hour: '19:00',
      sale: 0,
    },
    {
      hour: '20:00',
      sale: 0,
    },
    {
      hour: '21:00',
      sale: 0,
    },
    {
      hour: '22:00',
      sale: 0,
    },
    {
      hour: '23:00',
      sale: 0,
    },
  ],
  topProducts: [],
  topCustomers: [],
};

export async function getStorePreferences(storeId: number) {
  const { storePreferences } = (await apigateway.get(
    `/store-preferences?store=${storeId}`,
  )) as any;

  return storePreferences;
}

export async function getDashboardStats(storeId: number) {
  const { dashboardStats } = (await apigateway.get(
    `/dashboard-stats?store=${storeId}`,
  )) as any;

  return dashboardStats;
}

export const getPaymentPlans = async () => {
  return apigateway.get('/plans');
};

export const getCalculatedTaxRate = (tax: Tax[], storeTaxes: Tax[]): number => {
  let taxRate = 0;
  if (tax && tax.length > 1) {
    // const parentTaxId = tax?.[0]?.parentId;
    // taxRate =
    //   storeTaxes.find(
    //     fndTax => fndTax.id?.toString() === parentTaxId?.toString(),
    //   )?.rate || 0;
  } else {
    taxRate = tax?.[0]?.rate || 0;
  }

  return taxRate / 100;
};

export const getCalculatedDiscount = (
  discount: Discount | null,
  price = 0,
): number => {
  if (!discount) {
    return 0;
  }

  if (discount.type === 'fixed-amount') {
    return discount.value;
  }

  return (discount.value / 100) * price;
};

export const getSaleItem = (
  fndProduct: Product,
  storeListedTaxes: Tax[],
): SaleItem => {
  return {
    itemId: fndProduct.id,
    name: fndProduct.name,
    price: parseFloat(fndProduct.inventory.price?.toString()),
    cost: fndProduct.inventory.cost
      ? parseFloat(fndProduct.inventory.cost?.toString())
      : 0,
    stockQuantity: fndProduct.inventory.quantity,
    quantityBought: 1,
    upc: fndProduct.inventory.upc || null,
    sku: fndProduct?.inventory.sku,
    packPrice: fndProduct.inventory.trackWholesale
      ? fndProduct.inventory.packPrice || 0
      : 0,
    packQuantity: fndProduct.inventory.trackWholesale
      ? fndProduct.inventory.packQuantity || 0
      : 0,
    isTaxable: false,
    isWholesale: false,
    tax:
      fndProduct.taxes &&
      fndProduct.taxes.length > 0 &&
      storeListedTaxes.length > 0
        ? {
            amount: 0,
            rate: getCalculatedTaxRate(fndProduct.taxes, storeListedTaxes),
            calculatedTax: 0,
          }
        : null,
    discount: null,
    photos: fndProduct.photos,
  };
};

export const storePreferenceFormAction = async (
  path: string,
  method: 'post' | 'put',
  params: Record<string, unknown>,
) => {
  return apigateway[method](path, params);
};

export const getLocallySavedSales = () => {
  const localSales = JSON.parse(
    window.localStorage.getItem('_pos_sales') || '{}',
  ) as Sale[];

  return !isEmpty(localSales) ? localSales : [];
};

export const storeSaleLocally = (sale: Sale) => {
  let getLocallySales = JSON.parse(
    window.localStorage.getItem('_pos_sales') || '{}',
  ) as Sale[];

  getLocallySales = !isEmpty(getLocallySales) ? getLocallySales : [];

  const filterAndAddNewSale = [
    sale,
    ...getLocallySales.filter(fndSale => fndSale.id !== sale.id),
  ];

  window.localStorage.setItem(
    '_pos_sales',
    JSON.stringify(filterAndAddNewSale),
  );
};

export const removeLocallyStoredSale = (sale: Sale) => {
  let getLocallySales = JSON.parse(
    window.localStorage.getItem('_pos_sales') || '{}',
  ) as Sale[];

  getLocallySales = !isEmpty(getLocallySales) ? getLocallySales : [];

  let newSales = getLocallySales.filter(fndSale => fndSale.id !== sale.id);

  newSales = newSales.filter(fndSale => fndSale.items.length !== 0);

  window.localStorage.setItem('_pos_sales', JSON.stringify(newSales));
};

export const getOpenedCashRegister = () => {
  const cashRegister = JSON.parse(
    window.localStorage.getItem('_pos_opened_cash_register') || '{}',
  ) as CashRegister;

  return !isEmpty(cashRegister) ? cashRegister : undefined;
};

export const saveOpenedCashRegister = (cashRegister: CashRegister) => {
  window.localStorage.setItem(
    '_pos_opened_cash_register',
    JSON.stringify(cashRegister),
  );
};

export const removeOpenedCashRegister = () => {
  window.localStorage.removeItem('_pos_opened_cash_register');
};

export const onItemSearch = (
  event: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
  cbSetSearch: (
    search: SearchQueryType | ((search: SearchQueryType) => SearchQueryType),
  ) => void,
  run: (promise: Promise<unknown>) => void,
  path: string,
) => {
  const { name, value } = event.target;

  cbSetSearch(fndSearch => {
    const updatedSearch = {
      ...fndSearch,
      [name]: value,
    };

    debounceFetchSearches(path, updatedSearch, run);

    return updatedSearch;
  });
};

export const fetchSearchItems = (
  path: string,
  params?: Record<string, unknown>,
  run?: (promise: Promise<unknown>) => void,
): Promise<unknown> | undefined => {
  const fndParams = {
    ...params,
    filter: encodeURIComponent(JSON.stringify(params?.filter || {})),
  };

  const fetching = apigateway.get(path, fndParams);

  if (run) {
    run?.(fetching);
    return;
  }

  // eslint-disable-next-line consistent-return
  return fetching;
};

export const debounceFetchSearches = debounce(fetchSearchItems, 300);

export const onItemFilter = (
  filter: Record<string, unknown>,
  cbSetSearch: (
    search: SearchQueryType | ((search: SearchQueryType) => SearchQueryType),
  ) => void,
  run: (promise: Promise<unknown>) => void,
  path: string,
) => {
  cbSetSearch(fndSearch => {
    const updatedSearch = {
      ...fndSearch,
      ...(filter && { filter: { ...fndSearch.filter, ...filter } }),
    };

    fetchSearchItems(path, updatedSearch, run);

    return updatedSearch;
  });
};

export const onItemDelete = (path: string, params: Record<string, unknown>) => {
  return apigateway.delete(path, params);
};

export const onItemArchive = (
  path: string,
  params: Record<string, unknown>,
) => {
  return apigateway.post(path, params);
};

export const onItemRestore = (
  path: string,
  params: Record<string, unknown>,
) => {
  return apigateway.post(path, params);
};

export const onAllItemsChecked = (
  isChecked: boolean,
  cbSetItemsState: (items: any) => void,
  cbSetAllIsItemsChecked: (isItemsChecked: boolean) => void,
) => {
  cbSetAllIsItemsChecked(isChecked);

  cbSetItemsState((fndItems: any) => {
    const getCheckedItems = fndItems.map((fndItem: any) => ({
      ...fndItem,
      selected: isChecked,
    }));

    return getCheckedItems;
  });
};

export const onItemChecked = (
  items: Array<Required<{ selected: boolean }>>,
  itemId: string | number,
  itemKey: string,
  isChecked: boolean,
  cbSetItemsState: (items: any) => void,
  cbSetAllIsItemsChecked: (isItemsChecked: boolean) => void,
) => {
  cbSetItemsState((fndItems: any) => {
    const getCheckedItems = fndItems.map((fndItem: any) => ({
      ...fndItem,
      ...(fndItem?.[itemKey] &&
        fndItem?.[itemKey]?.toString() === itemId.toString() && {
          selected: isChecked,
        }),
    }));

    const totalCheckedItems = getCheckedItems.filter(
      (fndItem: any) => fndItem.selected,
    ).length;

    cbSetAllIsItemsChecked(items.length === totalCheckedItems);

    return getCheckedItems;
  });
};

export const initSubscription = (params: Record<string, unknown>) => {
  return apigateway.post(`/subscribe?store=${params?.storeId}`, params);
};

export const retryPayment = (params: Record<string, unknown>) => {
  return apigateway.post(`/retry-payment?store=${params?.storeId}`, params);
};

export const verifyTransaction = async () => {
  return apigateway.get('/verify-transaction');
};
